import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { SITE, CITIES } from '../data/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  'AC Repair', 'Heating Repair', 'Heat Pump Service', 'Plumbing Repair', 'Commercial Plumbing',
  'Indoor Air Quality', 'Maintenance / Tune-Up', 'Excavation / Trenching', 'Other',
];
const URGENCY_OPTIONS = ['Emergency — need help today', 'As soon as possible', 'Within this week', 'Flexible / planning ahead'];
const INSTALL_TYPES = [
  'New Air Conditioner', 'New Furnace / Heating System', 'Heat Pump', 'Full HVAC System (Heating + Cooling)',
  'Commercial HVAC', 'New Construction HVAC', 'New Construction / Commercial Plumbing', 'Water or Sewer Line',
];
const SYSTEM_AGE = ['Under 5 years', '5-10 years', '10-15 years', '15+ years', 'Not sure', 'New build — no system yet'];
const PROPERTY_TYPES = ['Residential', 'Commercial'];
const HOME_SIZES = ['Under 1,500 sq ft', '1,500-2,500 sq ft', '2,500-4,000 sq ft', 'Over 4,000 sq ft', 'Commercial space'];
const FINANCING_OPTIONS = ['Yes — interested in financing', 'No', 'Maybe — tell me more'];

const EMPTY = { name: '', phone: '', email: '', service: '', city: '', message: '', urgency: '', systemAge: '', propertyType: '', homeSize: '', financing: '' };

export default function LeadForm({ variant = 'service' }) {
  const isInstall = variant === 'install';
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, email: form.email || undefined, formType: variant });
      toast.success(
        isInstall
          ? `Estimate request received. We will call to schedule your free consultation. Need us sooner? Call ${SITE.phone}.`
          : `Service request received. Our team will call you back shortly. For urgent help, call ${SITE.phone}.`
      );
      setForm(EMPTY);
    } catch (err) {
      toast.error('Something went wrong sending your request. Please call us at ' + SITE.phone + '.');
    } finally {
      setSending(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-navy outline-none transition-all focus:border-brand focus:ring-2 focus:ring-brand/20';
  const labelCls = 'mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500';

  const Select = ({ id, testid, label, value, onChange, options, required = false, placeholder = 'Select one' }) => (
    <div>
      <label htmlFor={id} className={labelCls}>{label}{required ? ' *' : ''}</label>
      <select id={id} data-testid={testid} required={required} value={value} onChange={onChange} className={inputCls}>
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-8" data-testid={`lead-form-${variant}`}>
      <h3 className="font-display text-xl font-bold text-navy sm:text-2xl" data-testid="lead-form-heading">
        {isInstall ? 'Request a Free Install Estimate' : 'Schedule Service'}
      </h3>
      <p className="mt-1 text-sm text-slate-500">
        {isInstall ? 'Tell us about your project — estimates are always free.' : 'We typically respond the same business day.'}
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${variant}-name`} className={labelCls}>Name *</label>
          <input id={`${variant}-name`} data-testid="lead-form-name" required value={form.name} onChange={set('name')} className={inputCls} placeholder="Your name" />
        </div>
        <div>
          <label htmlFor={`${variant}-phone`} className={labelCls}>Phone *</label>
          <input id={`${variant}-phone`} data-testid="lead-form-phone" required type="tel" value={form.phone} onChange={set('phone')} className={inputCls} placeholder="(704) 555-0123" />
        </div>
        <div>
          <label htmlFor={`${variant}-email`} className={labelCls}>Email *</label>
          <input id={`${variant}-email`} data-testid="lead-form-email" required type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="you@email.com" />
        </div>
        <Select
          id={`${variant}-service`} testid="lead-form-service" required
          label={isInstall ? 'What are we installing?' : 'What needs service?'}
          value={form.service} onChange={set('service')}
          options={isInstall ? INSTALL_TYPES : SERVICE_TYPES}
          placeholder={isInstall ? 'Select a project type' : 'Select a service'}
        />
        {isInstall ? (
          <>
            <Select id="install-age" testid="lead-form-system-age" label="Current System Age" value={form.systemAge} onChange={set('systemAge')} options={SYSTEM_AGE} />
            <Select id="install-property" testid="lead-form-property-type" label="Property Type" value={form.propertyType} onChange={set('propertyType')} options={PROPERTY_TYPES} />
            <Select id="install-size" testid="lead-form-home-size" label="Home / Building Size" value={form.homeSize} onChange={set('homeSize')} options={HOME_SIZES} />
            <Select id="install-financing" testid="lead-form-financing" label="Interested in Financing?" value={form.financing} onChange={set('financing')} options={FINANCING_OPTIONS} />
          </>
        ) : (
          <Select id="service-urgency" testid="lead-form-urgency" label="How urgent is this?" value={form.urgency} onChange={set('urgency')} options={URGENCY_OPTIONS} />
        )}
        <div className="sm:col-span-2">
          <label htmlFor={`${variant}-city`} className={labelCls}>City / Town</label>
          <select id={`${variant}-city`} data-testid="lead-form-city" value={form.city} onChange={set('city')} className={inputCls}>
            <option value="">Select your area</option>
            {CITIES.map((c) => <option key={c.slug} value={c.name}>{c.name}, NC</option>)}
            <option value="Other">Other / Surrounding Area</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${variant}-message`} className={labelCls}>
            {isInstall ? 'Anything else about the project?' : 'Describe the issue'}
          </label>
          <textarea
            id={`${variant}-message`} data-testid="lead-form-message" rows={4} value={form.message} onChange={set('message')} className={inputCls}
            placeholder={isInstall ? 'Timeline, current equipment, comfort goals...' : 'Noises, leaks, no cooling, no heat — the more detail the better...'}
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="lead-form-submit-button"
        className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-display text-base font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
          isInstall ? 'bg-navy shadow-navy/30 hover:bg-navy-light' : 'bg-brand shadow-brand/30 hover:bg-brand-dark'
        }`}
      >
        <Send className="h-4 w-4" />
        {sending ? 'Sending...' : isInstall ? 'Get My Free Estimate' : 'Schedule My Service'}
      </button>
      <p className="mt-3 text-center text-xs text-slate-400">
        Prefer to talk? Call <a href={SITE.phoneHref} className="font-bold text-brand">{SITE.phone}</a>
      </p>
    </form>
  );
}
